import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Save } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';

const teamMemberSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Invalid email address'),
  role: z.enum(['admin', 'project_manager', 'developer', 'designer', 'qa', 'content_writer', 'client_success']),
  department: z.string().optional(),
  status: z.enum(['active', 'inactive', 'on_leave', 'terminated']),
  hourly_rate: z.number().min(0).optional(),
  avatar_url: z.string().url().optional().or(z.literal('')),
});

type TeamMemberFormData = z.infer<typeof teamMemberSchema>;

const TeamForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = !!id;

  const { data: member } = useQuery({
    queryKey: ['team-member', id],
    queryFn: async () => {
      if (!id) return null;
      // Mock data - replace with actual API call
      return {
        id: Number(id),
        name: 'Roman Siromskyi',
        email: 'leowebmark@gmail.com',
        role: 'admin',
        department: 'Management',
        status: 'active',
        hourly_rate: 150,
        avatar_url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Roman',
      };
    },
    enabled: isEdit,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<TeamMemberFormData>({
    resolver: zodResolver(teamMemberSchema),
    defaultValues: {
      name: '',
      email: '',
      role: 'developer',
      department: '',
      status: 'active',
      hourly_rate: 0,
      avatar_url: '',
    },
  });

  useEffect(() => {
    if (member) {
      setValue('name', member.name);
      setValue('email', member.email);
      setValue('role', member.role as any);
      setValue('department', member.department || '');
      setValue('status', member.status as any);
      setValue('hourly_rate', member.hourly_rate || 0);
      setValue('avatar_url', member.avatar_url || '');
    }
  }, [member, setValue]);

  const mutation = useMutation({
    mutationFn: async (data: TeamMemberFormData) => {
      // Mock API call - replace with actual implementation
      console.log(isEdit ? 'Updating member' : 'Creating member', data);
      await new Promise((resolve) => setTimeout(resolve, 1000));
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members'] });
      if (isEdit) {
        queryClient.invalidateQueries({ queryKey: ['team-member', id] });
      }
      toast.success(isEdit ? 'Member updated successfully' : 'Member created successfully');
      navigate('/team');
    },
    onError: (error) => {
      toast.error('Failed to save member');
      console.error(error);
    },
  });

  const onSubmit = (data: TeamMemberFormData) => {
    mutation.mutate(data);
  };

  const role = watch('role');
  const status = watch('status');

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/team')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold">{isEdit ? 'Edit' : 'Add'} Team Member</h1>
          <p className="text-muted-foreground mt-1">
            {isEdit ? 'Update team member information' : 'Add a new team member to your organization'}
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Card>
          <CardHeader>
            <CardTitle>Member Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name">
                Name <span className="text-red-500">*</span>
              </Label>
              <Input id="name" {...register('name')} placeholder="John Doe" />
              {errors.name && <p className="text-sm text-red-500">{errors.name.message}</p>}
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email">
                Email <span className="text-red-500">*</span>
              </Label>
              <Input id="email" type="email" {...register('email')} placeholder="john@example.com" />
              {errors.email && <p className="text-sm text-red-500">{errors.email.message}</p>}
            </div>

            {/* Role */}
            <div className="space-y-2">
              <Label htmlFor="role">
                Role <span className="text-red-500">*</span>
              </Label>
              <Select value={role} onValueChange={(value) => setValue('role', value as any)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="project_manager">Project Manager</SelectItem>
                  <SelectItem value="developer">Developer</SelectItem>
                  <SelectItem value="designer">Designer</SelectItem>
                  <SelectItem value="qa">QA Engineer</SelectItem>
                  <SelectItem value="content_writer">Content Writer</SelectItem>
                  <SelectItem value="client_success">Client Success</SelectItem>
                </SelectContent>
              </Select>
              {errors.role && <p className="text-sm text-red-500">{errors.role.message}</p>}
            </div>

            {/* Department */}
            <div className="space-y-2">
              <Label htmlFor="department">Department</Label>
              <Input id="department" {...register('department')} placeholder="Engineering" />
              {errors.department && <p className="text-sm text-red-500">{errors.department.message}</p>}
            </div>

            {/* Status */}
            <div className="space-y-2">
              <Label htmlFor="status">
                Status <span className="text-red-500">*</span>
              </Label>
              <Select value={status} onValueChange={(value) => setValue('status', value as any)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="on_leave">On Leave</SelectItem>
                  <SelectItem value="terminated">Terminated</SelectItem>
                </SelectContent>
              </Select>
              {errors.status && <p className="text-sm text-red-500">{errors.status.message}</p>}
            </div>

            {/* Hourly Rate */}
            <div className="space-y-2">
              <Label htmlFor="hourly_rate">Hourly Rate ($)</Label>
              <Input
                id="hourly_rate"
                type="number"
                step="0.01"
                {...register('hourly_rate', { valueAsNumber: true })}
                placeholder="75.00"
              />
              {errors.hourly_rate && <p className="text-sm text-red-500">{errors.hourly_rate.message}</p>}
            </div>

            {/* Avatar URL */}
            <div className="space-y-2">
              <Label htmlFor="avatar_url">Avatar URL</Label>
              <Input id="avatar_url" {...register('avatar_url')} placeholder="https://..." />
              {errors.avatar_url && <p className="text-sm text-red-500">{errors.avatar_url.message}</p>}
              <p className="text-xs text-muted-foreground">
                Optional: Provide a URL to the member's avatar image
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button type="submit" disabled={mutation.isPending}>
                <Save className="h-4 w-4 mr-2" />
                {mutation.isPending ? 'Saving...' : isEdit ? 'Update Member' : 'Create Member'}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate('/team')}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
};

export default TeamForm;
